<template>
  <!-- The planner's elevation profile (PlanTrailElevation), built from the
       same inputs — see combinedProfile — so the two pages read alike. -->
  <div class="elevation-profile-container" :class="{ collapsed: isCollapsed, expanded: isExpanded }">
    <ElevationProfile
      :data="chartData"
      :highlights="projectedHighlights"
      :title="profileTitle"
      :subtitle="viewSubtitle"
      :stats="stats"
      :get-map="mapGetter"
      :max-points="1000"
      :day-markers="combinedProfile.dayMarkers"
      pinned-tooltip
      show-high-point
      collapsible
      expandable
      :collapsed="isCollapsed"
      :expanded="isExpanded"
      @update:collapsed="isCollapsed = $event"
      @update:expanded="onExpandedChange"
      @highlight-click="onHighlightClick"
    >
      <template #headerLeft>
        <!-- Back to the whole hike, like the planner's back-to-all-routes button -->
        <button
          v-if="showBackButton"
          class="back-btn"
          type="button"
          :title="$t('hike_map.hike_trails_panel.view_full_trail')"
          :aria-label="$t('hike_map.hike_trails_panel.view_full_trail')"
          @click="$emit('view-changed', { type: 'full' })"
        >
          <span class="material-icons">arrow_back</span>
        </button>
      </template>
    </ElevationProfile>
  </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue';
import { MeasurementPrefUtility } from '../../utils/helpers/MeasurementPref';
import ElevationProfile from '../components/ElevationProfile.vue';
import { showBackToFullHike } from './helpers/activeView';
import {
  buildElevationChartData,
  type ElevationChartData,
} from '../../utils/elevation/buildElevationChartData';
import {
  buildCombinedRouteProfileInput,
  type CombinedRouteProfileInput,
} from '../../utils/elevation/combinedRouteProfile';
import {
  projectHighlightsOntoLine,
  type ProjectedHighlight,
} from '../../utils/elevation/projectHighlights';
import {
  elevationGainMetersForActiveView,
  durationMinutesForActiveView
} from '../../utils/elevation/plannedTrailStats';

interface ElevationStats {
  distanceMeters: number;
  gainMeters: number;
  lossMeters: number;
  durationSeconds: number;
}

export default defineComponent({
  name: 'HikeMapElevationProfile',
  components: { ElevationProfile },
  props: {
    geojson: {
      type: Object,
      default: null
    },
    activeView: {
      type: Object,
      default: () => ({ type: 'full' })
    },
    plannedTrails: {
      type: Array,
      default: () => []
    },
    hikeDays: {
      type: Array,
      default: () => []
    },
    // Hike highlights / waypoints to project onto the profile. Each item is the
    // raw hike-highlight shape ({ id/clientId, title, coordinates: [lon, lat],
    // dayNumber, ... }). Defaults to [] so parents that don't supply them keep
    // working. The parent (HikeMap.vue) should pass `:highlights="hikeHighlights"`.
    highlights: {
      type: Array as PropType<any[]>,
      default: () => []
    }
  },
  emits: ['view-changed'],
  data() {
    return {
      isCollapsed: false,
      isExpanded: false
    };
  },
  computed: {
    showBackButton(): boolean {
      return showBackToFullHike(this.activeView, this.plannedTrails.length);
    },

    profileTitle(): string {
      if (this.activeView.type === 'day') {
        const dayInfo = this.hikeDays.find((d: any) => d.dayNumber === this.activeView.dayNumber);
        return dayInfo?.name || this.$t('hike_map.day_number', { number: this.activeView.dayNumber });
      } else if (this.activeView.type === 'route') {
        const trail = this.getTrailById(this.activeView.trailId);
        return trail?.title || this.$t('hike_map.route');
      }
      return this.$t('shared.elevation');
    },

    viewSubtitle(): string {
      if (this.activeView.type === 'day') {
        const dayInfo = this.hikeDays.find((d: any) => d.dayNumber === this.activeView.dayNumber) as any;
        return dayInfo?.description || '';
      }
      return '';
    },

    // Build an ordering map from ptv2PlannedTrailId -> sort index using the
    // plannedTrails prop (same data source as the left panel, ordered by day then position).
    trailOrderMap(): Map<string, number> {
      const map = new Map<string, number>();
      [...(this.plannedTrails as any[])]
        .sort((a, b) => ((a.day ?? 0) - (b.day ?? 0)) || ((a.position ?? 0) - (b.position ?? 0)))
        .forEach((t, idx) => {
          const id = t.ptv2PlannedTrail?.id ?? t.id;
          if (id != null) map.set(String(id), idx);
        });
      return map;
    },

    filteredFeatures(): any[] {
      if (!this.geojson?.features) return [];

      let features = this.geojson.features;

      if (this.activeView.type === 'day') {
        // For day view: show only 'route' type within that day (exclude alt, spur, transport)
        features = features.filter((f: any) =>
          f.properties?.hikeDayNumber === this.activeView.dayNumber &&
          f.properties?.routeStyle === 'route'
        );
      } else if (this.activeView.type === 'route') {
        // For individual route: show just that route (any type including alt, spur, transport)
        features = features.filter((f: any) =>
          f.properties?.ptv2PlannedTrailId != null &&
          String(f.properties.ptv2PlannedTrailId) === String(this.activeView.trailId)
        );
      } else {
        // For full view: show only 'route' type (exclude alt, spur, transport)
        features = features.filter((f: any) =>
          f.properties?.routeStyle === 'route'
        );
      }

      // Sort features to match the left panel's day/position sequence.
      // The trailOrderMap is built from plannedTrails (hikesPlannedTrails join table data)
      // which is the authoritative ordering — the same data the left panel uses.
      if (this.trailOrderMap.size > 0) {
        features = [...features].sort((a: any, b: any) => {
          const aIdx = this.trailOrderMap.get(String(a.properties?.ptv2PlannedTrailId)) ?? 9999;
          const bIdx = this.trailOrderMap.get(String(b.properties?.ptv2PlannedTrailId)) ?? 9999;
          return aIdx - bIdx;
        });
      }

      return features;
    },

    multiLineStrings(): any[] {
      return this.filteredFeatures.filter(f => f.geometry?.type === 'MultiLineString');
    },

    // Same inputs the planner's profile is built from: distance restarting at
    // each route (no phantom hop across gaps between routes), surfaces for
    // the line colouring, and day-boundary markers on the whole-hike view.
    combinedProfile(): CombinedRouteProfileInput {
      const measurementPref = MeasurementPrefUtility.preference();
      const showDays = this.activeView.type !== 'day' && this.activeView.type !== 'route';
      return buildCombinedRouteProfileInput(
        this.multiLineStrings.map((feature: any) => ({
          coordinates: feature.geometry?.coordinates,
          surfaceRanges: feature.properties?.surfaces,
          day: showDays ? feature.properties?.hikeDayNumber : null
        })),
        (km: number) => measurementPref.bigUnitValue(km)
      );
    },

    // Delegate the actual profile computation (equal-distance downsampling,
    // 6m-threshold gain/loss, linear-distance points) to the shared builder,
    // at the planner's resolution.
    chartData(): ElevationChartData | null {
      const input = this.combinedProfile;
      if (!input.coordinates.length) return null;
      return buildElevationChartData({
        coordinates: input.coordinates,
        surfaces: input.surfaces as number[],
        distances: input.distances,
        maxPoints: 1000,
        measurementPref: MeasurementPrefUtility.preference()
      });
    },

    // Header stats. Gain/loss come from the shared builder (6m-threshold),
    // NOT the old naive positive-delta sum — this keeps gain consistent with
    // the planner.
    stats(): ElevationStats {
      const d = this.chartData;
      return {
        distanceMeters: d?.maxDistanceMeters ?? 0,
        // Same server-computed ptv2PlannedTrail.elevationGainMeters the routes
        // panel (and the apps) show, so the two headers on this page can't
        // disagree; the geometry figure is only the fallback for missing data.
        gainMeters: elevationGainMetersForActiveView(this.plannedTrails, this.activeView) ?? d?.elevationGainMeters ?? 0,
        lossMeters: d?.elevationLossMeters ?? 0,
        durationSeconds: this.totalDurationSeconds
      };
    },

    totalDurationSeconds(): number {
      // Route view shows that route's own duration whatever its style; day and
      // full views sum only the main 'route' segments (excluding alt, spur and
      // transport) — same view filtering the elevation gain total uses.
      return durationMinutesForActiveView(this.plannedTrails, this.activeView) * 60;
    },

    // Project hike highlights onto the active line. Highlights far from the line
    // are dropped by projectHighlightsOntoLine, so passing all of them is safe.
    projectedHighlights(): ProjectedHighlight[] {
      const { coordinates: coords, distances } = this.combinedProfile;
      const highlights = this.highlights as any[];
      if (coords.length < 2 || !highlights?.length) return [];

      const inputs = highlights
        .map((h) => {
          const c = h?.coordinates || [];
          return {
            id: h?.id ?? h?.clientId,
            name: h?.title || '',
            lon: c[0],
            lat: c[1],
            icon: undefined as string | undefined
          };
        })
        .filter((i) => typeof i.lon === 'number' && typeof i.lat === 'number' && i.id != null);

      return projectHighlightsOntoLine(coords, inputs, {
        maxDistanceMeters: this.chartData?.maxDistanceMeters,
        distances
      });
    }
  },
  methods: {
    getTrailById(trailId: string | number | undefined): any {
      for (const trail of this.plannedTrails) {
        if ((trail as any).ptv2PlannedTrail?.id === trailId) {
          return (trail as any).ptv2PlannedTrail;
        } else if ((trail as any).id === trailId) {
          return trail;
        }
      }
      return null;
    },

    // Lazy map accessor handed to the shared component so it manages the
    // .elevation-marker on hover. Bound to the instance by Vue.
    mapGetter(): any {
      return (window as any).map ?? null;
    },

    onExpandedChange(value: boolean) {
      this.isExpanded = value;
    },

    onHighlightClick(highlight: ProjectedHighlight) {
      const map = (window as any).map;
      if (!map || typeof map.flyTo !== 'function') return;
      try {
        map.flyTo({
          center: highlight.snappedLngLat,
          zoom: Math.max(typeof map.getZoom === 'function' ? map.getZoom() : 13, 13)
        });
      } catch (error) {
        console.error('Error panning to highlight:', error);
      }
    }
  }
});
</script>

<style scoped>
.elevation-profile-container {
  position: absolute;
  bottom: 40px;
  left: 20px;
  right: 20px;
  margin: auto;
  max-width: 630px;
  height: 180px;
  background: rgba(40, 42, 50, 0.95);
  backdrop-filter: blur(10px);
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.4);
  border-radius: 12px;
  z-index: 999;
  transition: all 0.3s ease;
  color: #fff;
}

.elevation-profile-container.collapsed {
  height: 48px;
}

.elevation-profile-container.expanded {
  height: 400px;
  max-width: calc(100% - 40px);
}

/* The shared ElevationProfile provides the header/chart; let the outer
   container own positioning + chrome (bg / shadow / rounded corners / height)
   so existing desktop + mobile-sheet styling keeps working. */
.elevation-profile-container :deep(.elevation-profile) {
  height: 100%;
  background: transparent;
  box-shadow: none;
  border-radius: 12px;
}

/* Back-to-whole-hike button in the profile header (same look as the planner's) */
.back-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: rgba(255, 255, 255, 0.75);
  cursor: pointer;
}

.back-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}

.back-btn .material-icons {
  font-size: 18px;
}

/* Mobile responsive */
@media (max-width: 768px) {
  .elevation-profile-container {
    left: 10px;
    right: 10px;
    height: 150px;
  }

  .elevation-profile-container.expanded {
    height: 300px;
  }
}
</style>
